export type Banner = {
	id: string;
	type: string;
	title?: string;
	content: string;
	url?: string;
	dismissible?: boolean;
	timestamp: number;
};

export enum TTS_RESPONSE_SPLIT {
	PUNCTUATION = 'punctuation',
	PARAGRAPHS = 'paragraphs',
	NONE = 'none'
}

export interface Message {
	id: string;
	parentId: string | null;
	childrenIds: string[];
	statusHistory: {
		action: string;
		description: string;
		done: boolean;
	}[];
	role: 'user' | 'assistant';
	content: string;
	timestamp: number;
	models?: string[];
	files?: any[];
	annotation?: {
		rating?: number;
	};
	originalContent?: string;
}

// Extend the Message interface to include our additional properties
export interface ExtendedMessage extends Message {
	model?: string;
	modelName?: string;
	modelIdx?: number;
	done?: boolean;
	error?: { content: string };
	info?: any;
	sources?: SourceResponse;
	code_executions?: any[];
}

export interface History {
	messages: Record<string, Message>;
	currentId: string | null;
}

export interface ChatForm {
	chat: {
		title?: string;
		models?: string[];
		params?: Record<string, any>;
		history: History;
		messages: Message[];
	};
}

/**
 * The response model for the sources we get at the end of the streaming response
 */
export type SourceResponse = {
	collections: Record<
		string,
		{
			id: string;
			name: string;
			description?: string;
			created_at: string;
			metadata?: Record<string, string>;
		}
	>;
	items: Record<
		string,
		{
			id: string;
			collection_id: string;
			type: string;
			name: string;
			source_url?: string;
			created_at: string;
			metadata?: Record<string, string>;
		}
	>;
	chunks: Record<
		string,
		{
			id: string;
			item_id: string;
			item_name?: string;
			content: string;
			chunk_index?: number;
			start_pos?: number;
			end_pos?: number;
			score?: number;
			metadata?: Record<string, string>;
		}
	>;
	hits: string[];
};

export type MetricsResponse = {
	period: string;
	timestamp: string;
	summary: {
		total_requests: number;
		total_users: number;
		active_users: number;
		active_sessions: number;
		error_rate: number;
		avg_response_time: number;
		total_chats: number;
		total_chat_sessions: number;
		total_knowledge_bases: number;
		knowledge_bases_created: number;
		admin_users: number;
	};
	user_activity: {
		total_users: number;
		admin_users: number;
		active_sessions: number;
		active_users: number;
		by_role: Record<string, number>;
		database_metrics: {
			total_registered_users: number;
			users_active_24h: number;
			users_active_7d: number;
			users_active_30d: number;
			new_users_24h: number;
			new_users_7d: number;
			new_users_30d: number;
			avg_last_login_age_hours: number;
			users_by_role: Record<string, number>;
			recent_logins: Array<{
				user_id: string;
				name: string;
				email: string;
				role: string;
				last_login: string;
				login_age_hours: number;
			}>;
		};
	};
	model_inference: {
		total_requests: number;
		success_rate: number;
		avg_tokens: number;
		by_model: Record<string, number>;
		by_status: Record<string, number>;
		messages_processed: number;
	};
	chat_activity: {
		sessions_created: number;
		messages_processed: number;
		streaming_requests: number;
		new_chats: number;
		database_metrics: {
			total_chats: number;
			chats_created_24h: number;
			chats_created_7d: number;
			chats_created_30d: number;
			archived_chats: number;
			pinned_chats: number;
			avg_messages_per_chat: number;
		};
	};
	knowledge_activity: {
		knowledges_created: number;
		documents_ingested: number;
		success_rate: number;
		knowledge_bases_created: number;
		database_metrics: {
			total_knowledge_bases: number;
			knowledge_bases_created_24h: number;
			knowledge_bases_created_7d: number;
			knowledge_bases_created_30d: number;
			total_items: number;
			items_created_24h: number;
			items_created_7d: number;
			items_created_30d: number;
			avg_items_per_knowledge: number;
		};
	};
	navigation: {
		back_to_app_url: string;
	};
};

export interface LLMChunkerSettings {
	min_words_target: number;
	max_words_target: number;
	min_words_hard_limit: number;
	max_words_hard_limit: number;
	chunker_model_id: string;
	embedding_model_id: string;
}

// Default values matching backend settings.py LLMChunkerSettings
export const DEFAULT_LLM_CHUNKER_SETTINGS: LLMChunkerSettings = {
	min_words_target: 300,
	max_words_target: 800,
	min_words_hard_limit: 150,
	max_words_hard_limit: 1200,
	chunker_model_id: '',
	embedding_model_id: 'intfloat/multilingual-e5-large-instruct'
};
